import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LetterView() {
  const { customerId, letterId } = useParams();
  const navigate = useNavigate();
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const fetchLetter = async () => {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/customer/${customerId}/letter/${letterId}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch letter');
        }

        const data = await response.json();
        setLetter(data.data || data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (letterId && customerId) {
      fetchLetter();
    }
  }, [letterId, customerId]);

  const handlePrintPDF = async () => {
    setPdfLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/customer/${customerId}/letter/${letterId}/pdf`
      );

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${letterId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error downloading PDF: ' + err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = async () => {
    try {
      // Fetch letterhead template
      const templateResponse = await fetch('/letterhead_template.html');
      const templateHTML = await templateResponse.text();

      // Create printable content with letterhead
      const printWindow = window.open('', '_blank');
      const contentDiv = document.getElementById('letter-content');
      const letterContentHTML = contentDiv ? contentDiv.innerHTML : letter.content;

      // Combine template with letter content
      const fullHTML = templateHTML.replace(
        '<div class="content">',
        `<div class="content">${letterContentHTML}`
      );

      printWindow.document.write(fullHTML);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 250);
    } catch (err) {
      // Fallback to simple print
      window.print();
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        <p>Loading letter...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: '#dc2626', marginBottom: '20px' }}>❌ {error}</p>
        <button
          onClick={() => navigate('/letters')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#1b7abf',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          ← Back to Letters
        </button>
      </div>
    );
  }

  if (!letter) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Letter not found</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: '0 0 5px 0', color: '#1b7abf' }}>{letter.title}</h2>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            {letter.letter_date && `Dated: ${new Date(letter.letter_date).toLocaleDateString()}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handlePrint}
            style={{
              padding: '10px 16px',
              backgroundColor: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🖨️ Print
          </button>
          <button
            onClick={handlePrintPDF}
            disabled={pdfLoading}
            style={{
              padding: '10px 16px',
              backgroundColor: pdfLoading ? '#ccc' : '#ea580c',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: pdfLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            {pdfLoading ? '⏳ Downloading...' : '📥 Download PDF'}
          </button>
        </div>
      </div>

      <div
        id="letter-content"
        style={{
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '30px',
          lineHeight: '1.8',
          color: '#333',
          fontFamily: 'Arial, sans-serif'
        }}
        dangerouslySetInnerHTML={{ __html: letter.content }}
      />

      <div style={{ marginTop: '20px' }}>
        <button
          onClick={() => navigate('/letters')}
          style={{
            padding: '10px 16px',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          ← Back to Letters
        </button>
      </div>
    </div>
  );
}
